import { useEffect, useRef, useState } from "react";
import { Mic, Send, Loader2, Square } from "lucide-react";
import { isSpeechSupported, startListening } from "../lib/speech";
import { getCsrfToken } from "../lib/auth";
import { WikiAutocompleteTextarea } from "./WikiAutocompleteTextarea";

/** Workspace-shell quick capture composer. Lives at the top of the
 *  Today cockpit on `/u/<handle>`. Two affordances:
 *    1. Type + send (POST /api/capture).
 *    2. Tap the mic, speak, transcript fills the input live, tap
 *       again to stop, review, hit send (Web Speech API; falls back
 *       to a clear "use the Capture page" hint when the API is not
 *       available, e.g. Firefox or old iOS Safari).
 *
 *  Keeps the user in flow on the cockpit instead of navigating to
 *  /capture for every quick thought. */
export function QuickCapture({ onCaptured }: { onCaptured?: () => void }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recordedAt, setRecordedAt] = useState<string | null>(null);
  const baseRef = useRef("");
  const stopRef = useRef<(() => void) | null>(null);
  const startedAtRef = useRef<number | null>(null);

  // Cleanup the in-flight recogniser if the user navigates away mid-speech.
  useEffect(() => {
    return () => {
      stopRef.current?.();
    };
  }, []);

  const startVoice = () => {
    if (listening) return;
    setError(null);
    baseRef.current = text ? text + " " : "";
    startedAtRef.current = Date.now();
    setRecordedAt(new Date().toISOString());
    const handle = startListening({
      onPartial: (heard) => setText((baseRef.current + heard).trim()),
      onError: (msg) => setError(`mic: ${msg}`),
    });
    if (!handle) {
      setError(
        "Voice input is not supported here. Type your capture or use Capture from the menu.",
      );
      return;
    }
    setListening(true);
    stopRef.current = handle.stop;
    handle.done.then(() => {
      setListening(false);
      stopRef.current = null;
    });
  };

  const stopVoice = () => {
    stopRef.current?.();
    setListening(false);
  };

  const send = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    setError(null);
    try {
      const csrf = getCsrfToken();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (csrf) headers["X-CSRF-Token"] = csrf;

      // The mic flow lands as a voice capture (better intent hints,
      // duration metadata). Typed text goes through the normal capture
      // route so the pipeline classifier picks the right type.
      const usedVoice = !!recordedAt;
      const url = usedVoice ? "/api/capture/voice" : "/api/capture";
      const payload = usedVoice
        ? {
            transcript: body,
            source: "voice-pwa",
            scope: "personal",
            kind: "auto",
            recorded_at: recordedAt,
            duration_ms: startedAtRef.current ? Date.now() - startedAtRef.current : null,
          }
        : { text: body, type: "thought", scope: "personal" };

      const res = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${body.slice(0, 200)}`);
      }
      setText("");
      setRecordedAt(null);
      startedAtRef.current = null;
      onCaptured?.();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const supported = isSpeechSupported();

  return (
    <form
      onSubmit={send}
      className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-3 sm:p-4 space-y-2"
    >
      <div className="flex items-end gap-2">
        <WikiAutocompleteTextarea
          value={text}
          onChange={setText}
          onKeyDown={(e) => {
            // Cmd/Ctrl+Enter submits without forcing the user to click.
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              send();
            }
          }}
          rows={listening ? 3 : 2}
          placeholder={
            listening
              ? "Listening... tap the mic to stop"
              : "Capture a thought, task, or note..."
          }
          className="flex-1 resize-none rounded-xl bg-white/60 dark:bg-slate-900/40 border border-slate-200/80 dark:border-slate-700/60 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-400"
        />
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          {supported ? (
            <button
              type="button"
              onClick={listening ? stopVoice : startVoice}
              aria-label={listening ? "Stop recording" : "Start recording"}
              className={
                listening
                  ? "rounded-full w-10 h-10 inline-flex items-center justify-center bg-rose-500 hover:bg-rose-600 text-white shadow-md ring-2 ring-rose-300/40 animate-pulse"
                  : "rounded-full w-10 h-10 inline-flex items-center justify-center bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 transition-colors"
              }
            >
              {listening ? <Square className="w-4 h-4" fill="currentColor" /> : <Mic className="w-4 h-4" />}
            </button>
          ) : null}
          <button
            type="submit"
            disabled={!text.trim() || busy}
            aria-label="Send capture"
            className="rounded-full w-10 h-10 inline-flex items-center justify-center bg-sky-600 hover:bg-sky-700 text-white shadow-md disabled:opacity-50 transition-colors"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 text-[11px] text-slate-500 dark:text-slate-400">
        <span>
          {listening ? (
            <span className="text-rose-500 dark:text-rose-300 font-semibold">
              Recording... speak naturally
            </span>
          ) : (
            <span>
              {supported ? "Tap mic to dictate" : "Voice input not supported in this browser"}
              {" · "}Cmd/Ctrl+Enter to send
            </span>
          )}
        </span>
        {error && <span className="text-rose-600 dark:text-rose-400">{error}</span>}
      </div>
    </form>
  );
}
